import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useOnboardingStore } from '../onboardingStore';
import { getHintsForRoute } from '../data/contextualHints';

/**
 * Evaluates which contextual hint to show for the current route.
 * Shows hints only on first visit to a page, one at a time, with a delay.
 */
export function useContextualHint() {
  const location = useLocation();
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const {
    hasCompletedWelcome,
    activeTour,
    dismissedHints,
    seenPages,
    activeHint,
    showHint,
    markPageSeen,
  } = useOnboardingStore();

  useEffect(() => {
    // Clean up any pending timer when route changes
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }

    // Don't show hints during welcome or tours
    if (!hasCompletedWelcome || activeTour) return;

    // Don't show hints if one is already active
    if (activeHint) return;

    const path = location.pathname;

    // Mark page as seen
    markPageSeen(path);

    // Check if this is a first visit (wasn't in seenPages before)
    const isFirstVisit = !seenPages.includes(path);
    if (!isFirstVisit) return;

    // Get applicable hints for this route
    const hints = getHintsForRoute(path);
    const undismissedHint = hints.find((h) => !dismissedHints.includes(h.id));

    if (!undismissedHint) return;

    // Show the hint after a delay
    const delay = undismissedHint.delay ?? 1000;
    timerRef.current = setTimeout(() => {
      // Verify the target exists before showing
      const target = document.querySelector(undismissedHint.targetSelector);
      if (target) {
        showHint(undismissedHint.id);
      }
    }, delay);

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, hasCompletedWelcome, activeTour]);
}
